import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useForm } from "react-hook-form";
import { JobClientFeedbackFormValues, defaultJobClientFeedbackValues, jobClientFeedbackSchema, mapClientFeedbackForBackend } from "./client-feedback-types";
import { Card, CardContent } from "../ui/card";
import { Form, FormField, FormLabel, Textarea, Button, FormMessage, FormInput } from "../ui";
import { View } from "react-native";
import { trpc } from "@/trpc/react";
import Toast from "react-native-toast-message";
import { P } from "../ui/typography";
import { useCrew } from "@/context/CrewProvider";
import { StarRating } from "@/components/ui/star-rating";

interface ClientFeedbackFormProps {
  jobId: number;
  jobDateId: number;
}

export default function ClientFeedbackForm({ jobId, jobDateId }: ClientFeedbackFormProps) {
  const router = useRouter();

  const form = useForm<JobClientFeedbackFormValues>({
    resolver: zodResolver(jobClientFeedbackSchema),
    defaultValues: defaultJobClientFeedbackValues,
  });

  const { mutate: createClientFeedback } = trpc.clientFeedback.create.useMutation({
    onSuccess(_response) {
      router.back();
      Toast.show({
        type: 'success',
        text1: 'Client feedback successfully submitted!'
      });
    },
    onError(error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to submit client feedback',
        text2: error.message
      });
    }
  });

  const { currentCrewMember } = useCrew();
  const authorID = currentCrewMember?.id || 0;

  async function onSubmit(data: JobClientFeedbackFormValues) {
    const mappedValues = mapClientFeedbackForBackend(
      { ...data, job_id: jobId, job_date_id: jobDateId },
      authorID
    );
    createClientFeedback(mappedValues);
  }

  return (
    <Form {...form}>
      <Card className='mt-4 mb-4'>
        <CardContent className="gap-6">
          <View className="gap-4">
            <P className="text-sm text-muted-foreground mt-4">
              Please provide detailed feedback about the client, including communication quality,
              cooperation, professionalism, or any concerns that could affect future jobs with this client.
            </P>

            <FormField
              control={form.control}
              name="feedback"
              render={({ field }) => (
                <View className="gap-2">
                  <FormLabel>Client Feedback</FormLabel>
                  <Textarea
                    {...field}
                    className="min-h-[120px]"
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                  />
                  <FormMessage />
                </View>
              )}
            />

            <View className='flex flex-row items-center gap-4'>
              <FormLabel className='w-40'>Overall Client Rating</FormLabel>
              <FormField
                control={form.control}
                name="overall_client_rating"
                render={({ field }) => (
                  <View className="w-[160px]">
                    <StarRating
                      count={5}
                      size={20}
                      onFinishRating={(rating) => field.onChange(rating)}
                      selectedColor="#FFD700"
                      defaultRating={field.value as number || 0}
                    />
                    <View className="h-8">
                      <P className="text-center mt-2 text-xs">
                        {!field.value && "Not rated"}
                        {field.value === 1 && "Terrible - needs addressing"}
                        {field.value === 2 && "Below expectation"}
                        {field.value === 3 && "Meets expectation"}
                        {field.value === 4 && "Above expectation"}
                        {field.value === 5 && "Outstanding"}
                      </P>
                    </View>
                    {typeof field.value === 'number' && field.value > 0 && (
                      <View className="items-center mt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onPress={() => field.onChange(null)}
                          className="px-2 py-1"
                        >
                          Clear
                        </Button>
                      </View>
                    )}
                  </View>
                )}
              />
            </View>

            <FormField
              control={form.control}
              name="pm_name"
              render={({ field }) => (
                <View className="gap-2">
                  <FormLabel>Project Manager Name (Optional)</FormLabel>
                  <FormInput
                    {...field}
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                    placeholder="Enter project manager name"
                  />
                  <FormMessage />
                </View>
              )}
            />

            <FormField
              control={form.control}
              name="pm_notes"
              render={({ field }) => (
                <View className="gap-2">
                  <FormLabel>Project Manager Notes (Optional)</FormLabel>
                  <Textarea
                    {...field}
                    className="min-h-[100px]"
                    onChangeText={(value) => {
                      field.onChange(value);
                    }}
                    value={field.value || ''}
                    placeholder="Enter notes about the project manager"
                  />
                  <FormMessage />
                </View>
              )}
            />

            <View className='flex flex-row items-center gap-4'>
              <FormLabel className='w-40'>Overall PM Rating</FormLabel>
              <FormField
                control={form.control}
                name="overall_pm_rating"
                render={({ field }) => (
                  <View className="w-[160px]">
                    <StarRating
                      count={5}
                      size={20}
                      onFinishRating={(rating) => field.onChange(rating)}
                      selectedColor="#FFD700"
                      defaultRating={field.value as number || 0}
                    />
                    <View className="h-8">
                      <P className="text-center mt-2 text-xs">
                        {!field.value && "Not rated"}
                        {field.value === 1 && "Terrible - needs addressing"}
                        {field.value === 2 && "Below expectation"}
                        {field.value === 3 && "Meets expectation"}
                        {field.value === 4 && "Above expectation"}
                        {field.value === 5 && "Outstanding"}
                      </P>
                    </View>
                    {typeof field.value === 'number' && field.value > 0 && (
                      <View className="items-center mt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onPress={() => field.onChange(null)}
                          className="px-2 py-1"
                        >
                          Clear
                        </Button>
                      </View>
                    )}
                  </View>
                )}
              />
            </View>
          </View>

          <Button
            onPress={form.handleSubmit(onSubmit)}
            className="mt-6"
            variant="crew"
          >
            Submit Client Feedback
          </Button>
        </CardContent>
      </Card>
    </Form>
  );
}
